import { useEffect } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { AuthForm } from "@/components/auth-form"
import { Logo } from "@/components/logo"
import {
  EMPLOYER_PERSONAL_EMAIL_MESSAGE,
  employerPersonalEmailBlocked,
  navigateSafeReturn,
  roleHome,
  signOutSucceeded,
} from "@/lib/auth-client"
import { useAuthGate } from "@/hooks/use-auth-gate"
import { seekerHomePath } from "@/lib/candidate"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "sonner"

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => ({
    returnTo: typeof search.returnTo === "string" ? search.returnTo : undefined,
  }),
  component: LoginPage,
})

function LoginPage() {
  const { returnTo } = Route.useSearch()
  const gate = useAuthGate()
  const navigate = useNavigate()
  const isAdminReturnTo =
    returnTo === "/admin" || returnTo?.startsWith("/admin/") === true

  useEffect(() => {
    if (isAdminReturnTo) {
      void navigate({ to: "/admin/login", search: { returnTo } })
      return
    }
    if (gate.status === "no-profile") {
      // Account exists but no portal profile yet — silent bridge applies a
      // stashed/inferred join role, or shows Recruiter / Candidate signup.
      void navigate({
        to: "/onboarding/role",
        search: returnTo ? { returnTo } : {},
      })
      return
    }
    if (gate.status === "ready") {
      if (employerPersonalEmailBlocked(gate.profile)) {
        void signOutSucceeded().then(() => {
          toast.error(EMPLOYER_PERSONAL_EMAIL_MESSAGE)
        })
        return
      }
      if (gate.profile.role === "seeker") {
        void navigate({ to: seekerHomePath(gate.profile, returnTo) })
        return
      }
      navigateSafeReturn(navigate, returnTo, roleHome(gate.profile.role))
    }
  }, [gate, returnTo, navigate, isAdminReturnTo])

  if (isAdminReturnTo) {
    return (
      <div className="mkt-auth home-wf">
        <Skeleton className="h-40 w-full max-w-sm" />
      </div>
    )
  }

  if (gate.status !== "anonymous") {
    return (
      <div className="mkt-auth home-wf">
        <Skeleton className="h-40 w-full max-w-sm" />
      </div>
    )
  }

  return (
    <div className="mkt-auth home-wf">
      <Link to="/" className="home-wf-brand" aria-label="Hire home">
        <Logo />
      </Link>
      <div className="mkt-auth-card">
        <AuthForm mode="login" returnTo={returnTo} />
      </div>
    </div>
  )
}
